import type { ReactNode } from 'react';
import { Svg } from '../../primitives/Svg.js';
import { useReducedMotion } from '../../../theme/hooks.js';
import type { AnimatedIconProps } from '../../primitives/types.js';

/** Hand-drawn logarithmic spiral. Draws itself out from the centre on
 *  mount (paper-easing), then rotates very slowly forever so the eye
 *  catches motion only when it lingers. Best as a tiny aside — at 14–18px
 *  it reads as a nautilus glyph; at 24px+ it becomes its own focal point. */
export function IcnSpiral(props: AnimatedIconProps): ReactNode {
  const { active = true, ...rest } = props;
  const reduced = useReducedMotion();
  // Total path length tuned for the curve below — used as the dasharray.
  const total = 28;

  return (
    <Svg {...rest} iconName="spiral" data-on={active ? 'true' : 'false'}>
      <g
        style={{ transformOrigin: '8px 8px' }}
      >
        <path
          d="M8 8 m0 0 c1.2 0 1.6 1 1 1.8 c-0.6 0.8 -2 0.4 -2.4 -0.6 c-0.5 -1.4 1 -3 2.6 -2.6 c1.8 0.4 2.6 2.4 2 4 c-0.7 1.8 -3 2.4 -4.6 1.5 c-2 -1.2 -2.4 -4 -1 -5.8 c1.6 -2.2 5 -2.4 7 -0.4"
          strokeDasharray={total}
          strokeDashoffset={reduced || !active ? 0 : total}
          strokeLinecap="round"
        >
          {!reduced && active && (
            <animate
              key={active ? 'on' : 'off'}
              attributeName="stroke-dashoffset"
              from={total}
              to="0"
              dur="1.6s"
              fill="freeze"
              calcMode="spline"
              keySplines="0.22 1 0.36 1"
            />
          )}
        </path>
        {!reduced && active && (
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 8 8"
            to="360 8 8"
            dur="22s"
            begin="1.6s"
            repeatCount="indefinite"
          />
        )}
      </g>
    </Svg>
  );
}
